import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { Search, Droplets, LogOut, Settings, Menu } from 'lucide-react'
import { Avatar } from '@/components/ui/Avatar'
import GlobalSearchModal from '@/components/Search/GlobalSearchModal'
import { HeaderSearch } from '@/components/Search/HeaderSearch'
import { NotificationDropdown } from './NotificationDropdown'
import { useUIStore } from '@/stores/uiStore'
import { cn } from '@/lib/utils'
import { useScrollHide } from '@/hooks/useScrollHide'

function UserMenu() {
  const [open, setOpen] = useState(false)
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="User menu"
        aria-expanded={open}
        className="flex items-center gap-2 px-1.5 py-1 rounded-full hover:bg-white/[0.04] transition-colors"
      >
        <Avatar name={user?.name} size="sm" />
        <span className="hidden lg:block text-sm text-ink-300 max-w-[120px] truncate pr-1">{user?.name}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-56 glass-popup py-1 z-50 animate-fade-up">
            <div className="px-3 py-2 hairline-b mb-1">
              <p className="text-sm font-medium text-ink-100 truncate">{user?.name}</p>
              <p className="text-xs text-white/40 truncate">{user?.email}</p>
            </div>
            <Link
              to="/settings"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-3 py-2 text-sm text-ink-300 hover:text-ink-50 hover:bg-white/[0.04] transition-colors"
            >
              <Settings className="w-4 h-4" /> Settings
            </Link>
            <div className="my-1 border-t border-white/[0.06]" />
            <button
              onClick={async () => { setOpen(false); await logout(); navigate('/login') }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#E1A29A] hover:text-[#EFBFB9] hover:bg-[#A84B3E]/10 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export function Topbar() {
  const [searchModalOpen, setSearchModalOpen] = useState(false)
  const toggleMobileMenu = useUIStore((s) => s.toggleMobileMenu)
  const headerHidden = useScrollHide()

  // Global ⌘K / Ctrl+K shortcut — toggle search modal
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchModalOpen(prev => !prev)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  return (
    <header
      // Mobile (<xl): fixed overlay so translateY(-100%) vacates viewport space without layout shift.
      // Desktop (xl+): returns to static flex flow (xl:static) and is always visible (xl:translate-y-0).
      className={cn(
        'flex items-center gap-4 backdrop-blur-2xl hairline-b shrink-0',
        // Mobile positioning
        'fixed top-0 left-0 right-0 z-50',
        // Desktop: undo fixed, no forced translate
        'xl:static xl:z-auto xl:translate-y-0',
        // Smooth hide/show — disabled when user prefers reduced motion
        'motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-in-out',
        // Hide when scrolling down on mobile; xl: prefix keeps desktop header visible
        headerHidden && '-translate-y-full xl:translate-y-0',
      )}
      // Reads safe-area insets via the global :root CSS vars in index.css
      // (--safe-top / --safe-left / --safe-right). The vars are the canonical
      // way to consume the insets in styles where wrapping with SafeAreaTop /
      // SafeAreaInsets isn't structurally clean (the <header> needs its own
      // height + padding). CLAUDE.md §8.
      style={{
        height: 'calc(3.5rem + var(--safe-top))',
        paddingTop: 'var(--safe-top)',
        paddingLeft: 'max(1.5rem, var(--safe-left))',
        paddingRight: 'max(1.5rem, var(--safe-right))',
        background: 'var(--midnight-bg, #000000)',
      }}
    >
      {/* Hamburger — mobile only; opens the MobileMenu side drawer */}
      <button
        onClick={toggleMobileMenu}
        className="md:hidden -ml-1 w-10 h-10 flex items-center justify-center text-ink-400 hover:text-ink-50 hover:bg-white/[0.04] rounded-full transition-colors shrink-0"
        aria-label="Open navigation menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile logo */}
      <div className="md:hidden flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-white/[0.06] border border-white/[0.10] flex items-center justify-center">
          <Droplets className="w-4 h-4 text-ink-100" />
        </div>
        <span className="font-display text-xl text-ink-50 tracking-tightest">Flo<span className="italic-accent text-ink-300">·</span>Rite</span>
      </div>

      <div className="flex-1" />

      {/* Search — mobile: icon opens full-screen modal; desktop (xl+): inline expandable input */}
      <button
        onClick={() => setSearchModalOpen(true)}
        className="xl:hidden w-10 h-10 flex items-center justify-center text-ink-300 hover:text-ink-50 hover:bg-white/[0.04] rounded-full transition-all shrink-0"
        aria-label="Open global search"
      >
        <Search className="w-5 h-5" />
      </button>
      <HeaderSearch />

      {/* Notifications */}
      <NotificationDropdown />

      {/* User */}
      <UserMenu />

      {/* Global search modal */}
      <GlobalSearchModal
        isOpen={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
      />
    </header>
  )
}
